import {SpriteGraphics} from '@fantasmagames/core/src/display/SpriteGraphics';
import {HTMLCreationUtils} from '@fantasmagames/core/src/html/HTMLCreationUtils';
import {ScreenParams} from '@fantasmagames/core/src/screen/fullscreen/ScreenParams';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {Scrollbar} from '@fantasmagames/slot/src/modules/popupmenus/Scrollbar';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';
import {Error} from 'tslint/lib/error';

export class HTMLPageView extends View {
	protected _background: PIXI.Texture;
	protected _gameCanvas: HTMLElement;

	protected _uiStageOffsetY: number = 0;
	protected _uiStageMaxY: number = 0;

	protected _mainContainer: HTMLElement;
	protected _mainBody: HTMLElement;

	protected _hasLoaded: boolean = false;

	////////////////////////////////////////////////////////////////////
	// IOS touch and input.
	////////////////////////////////////////////////////////////////////
	protected onDragStopBoundFunction: (this: Document, ev: TouchEvent) => any;
	protected _dragInput: PIXI.Sprite;
	protected _yOffset: number = 0;
	protected _dragStartY: number;
	protected _minYDrag: number = 0;
	protected _maxYDrag: number = 0;
	protected _isDragging: boolean;

	protected _resourcesPath: string = 'HTML/';

	protected _containerId: string = 'FANTASMA_CONTAINER';
	protected _scrollBar: Scrollbar;

	public init(): void {
		super.init();

		this.setupPathsAndIds();

		this._gameCanvas = document.getElementById('GameCanvas');

		this.setupUiOffset();
		this.setupHTML();
		this.hide();

		if (!__PRODUCTION__) {
			(window as any).isGameRulesVisible = () => {
				return this._mainContainer.style.visibility === 'visible';
			};
		}
	}

	public show(): void {
		super.show();

		this._mainContainer.style.display = this.device.isIE || this.device.isMacOs ? 'block' : 'grid';
		if (this.device.isIOSMobileRequestingDesktop) {
			this._mainContainer.style.display = 'grid';
		}

		this._mainContainer.style.visibility = 'visible';

		if (this.device.isMobile || this.device.hasTouch) {
			this._minYDrag = this._mainBody.offsetHeight - this.resolutionManager.halfResolution.height * 0.5;
			this._minYDrag *= -1;

			if (!this._scrollBar) {
				this.addScrollbar();
			}
		}
	}

	public hide(): void {
		super.hide();

		this._mainContainer.style.display = 'none';
		this._mainContainer.style.visibility = 'hidden';
	}

	public onResized(screenParams: ScreenParams): void {
		this.resizeHTML();
	}

	/** Prefer not to use this. */
	public spriteSheetTextureToHTMLImage(texture: PIXI.Texture, className: string): HTMLElement {
		const atlasCropper = document.createElement('div');
		const imageDiv = document.createElement('img');
		atlasCropper.appendChild(imageDiv);

		atlasCropper.className = className;
		imageDiv.className = 'croppedImage';

		imageDiv.style.webkitTransform = `translateX(-${texture.orig.x}px) translateY(-${texture.orig.y})`;
		imageDiv.style.transform = `translateX(-${texture.orig.x}px) translateY(-${texture.orig.y})`;
		imageDiv.src = texture.baseTexture.imageUrl;

		return atlasCropper;
	}

	protected setupPathsAndIds(): void {
	}

	protected setupUiOffset(): void {
		const menuSkeleton = this.resources.getSpine('MAIN_MENU.json');

		const yPosTop = this.spineUtils.getBoneAbsolutePosition(menuSkeleton, 'TEXT_CUT_POSITION_TOP').y;
		const yPosBottom = this.spineUtils.getBoneAbsolutePosition(menuSkeleton, 'TEXT_CUT_POSITION_BOTTOM').y;

		this._uiStageOffsetY = this.resolutionManager.halfResolution.height - yPosTop;
		this._uiStageMaxY = this.resolutionManager.halfResolution.height - yPosBottom;
	}

	protected setupHTML(): void {
		this.setupMainContainer();

		this.setupMainDiv();
		this._mainContainer.appendChild(this._mainBody);

		this._gameCanvas.parentElement.appendChild(this._mainContainer);

		this.loadCss();

		this.resizeHTML();

		this._mainContainer.style.transformOrigin = `0px 0px`;
		const totalHeight: number = this._uiStageMaxY - this._uiStageOffsetY;
		this._mainContainer.style.maxHeight = this._mainContainer.style.height = `${totalHeight}px`;
	}

	protected loadCss(): void {
		// Add check for file loaded or not.
		HTMLCreationUtils.loadJsCssFile(this.resources.getDevicePathForResource('HTML/STYLE.css'), 'css');
	}

	protected setupMainContainer(): void {
		this._mainContainer = document.createElement('div');
		this._mainContainer.id = this._containerId;
		this._mainContainer.style.display = 'none';
		this._mainContainer.style.position = 'absolute';
		this._mainContainer.style.visibility = 'hidden';

		this._mainContainer.style.height = this.resolutionManager.resolution.height - this._uiStageOffsetY + 'px';
		this._mainContainer.style.width = this.resolutionManager.resolution.width + 'px';
	}

	protected setupMainDiv(): void {
		this._mainBody = document.createElement('div');
		this._mainBody.innerHTML = this.getBodyContent();
		this._mainBody.id = 'FANTASMA_PAYTABLE';

		// this._mainBody.style.width = '100%';
		this._mainBody.style.width = this.device.isMobile ? this._mainContainer.style.width : '100%';
		this._mainBody.style.height = '100%';

		this.onDivComplete();
	}

	protected onDivComplete(): void {
		this._hasLoaded = true;

		this.replaceImages();

		if (this.device.isMobile || this.device.hasTouch) {
			this.setupForIOS();
		} else {
			this.setupInputForDesktop();
		}
	}

	protected getIframeContent(): string {
		throw new Error('Needs implementation.');
	}

	protected getBodyContent(): string {
		throw new Error('Needs implementation.');
	}

	/**
	 * Only use for Iframe which is unpreffered.
	 */
	protected onLoadComplete(): void {
		this._hasLoaded = true;

		// const iframeBody = this._mainIframe.contentDocument.body;
		// this._mainBody = iframeBody;

		if (this.device.iOS || this.device.iPad || this.device.hasTouch) {
			this.setupForIOS();
		}

		this.replaceImages();
	}

	protected setupInputForDesktop(): void {
		this._mainContainer.style.overflowY = 'scroll';
		this._mainContainer.style.webkitUserSelect = 'none';
		this._mainContainer.style.userSelect = 'none';
		this._mainContainer.style.msUserSelect = 'none';
		this._mainContainer.style.touchAction = 'none';

		this._mainBody.style.pointerEvents = 'none';
		this._mainBody.style.touchAction = 'none';
	}

	protected setupForIOS(): void {
		// this._mainContainer.appendChild(this._mainBody);

		this._mainContainer.style.overflowY = 'scroll';
		this._mainContainer.style.pointerEvents = 'none';
		this._mainContainer.style.touchAction = 'none';

		this._mainBody.style.pointerEvents = 'none';
		this._mainBody.style.touchAction = 'none';

		this.setupInput();
	}

	protected addScrollbar(): void {
		const contentHeight: number = this._uiStageMaxY - this._uiStageOffsetY;
		const scrollBarWidth: number = 12;

		this._scrollBar = new Scrollbar(scrollBarWidth, contentHeight, contentHeight, this._mainBody.scrollHeight);
		this.container.addChild(this._scrollBar);
		this._scrollBar.position.set(this.resolutionManager.resolution.width - scrollBarWidth, this._uiStageOffsetY);
	}

	protected setupInput(): void {
		this._dragInput = SpriteGraphics.makeHiddenRectangle(
			this.resolutionManager.resolution.width,
			this._uiStageMaxY - this._uiStageOffsetY,
		);

		this._dragInput.interactive = true;
		this.container.addChild(this._dragInput);
		this._dragInput.position.y = this._uiStageOffsetY;
		this._dragInput.on('touchstart', this.onInputTouchStart.bind(this));
		this._dragInput.on('mousedown', this.onInputTouchStart.bind(this));

		this.onDragStopBoundFunction = this.removeInput.bind(this);
	}

	protected onInputTouchStart(event: PIXI.interaction.InteractionEvent, ...args: any[]): void {
		if (this._isDragging) {
			return;
		}

		this._isDragging = true;

		document.addEventListener('touchend', this.onDragStopBoundFunction);
		document.addEventListener('touchcancel', this.onDragStopBoundFunction);
		document.addEventListener('mouseup', this.onDragStopBoundFunction);
		document.addEventListener('mouseleave', this.onDragStopBoundFunction);
		document.addEventListener('mouseout', this.onDragStopBoundFunction);

		this._dragStartY = event.data.global.y;

		this._dragInput.on('touchmove', this.onInputMove.bind(this));
		this._dragInput.on('mousemove', this.onInputMove.bind(this));
	}

	protected removeInput(): void {
		document.removeEventListener('touchend', this.onDragStopBoundFunction);
		document.removeEventListener('touchcancel', this.onDragStopBoundFunction);

		document.removeEventListener('mouseup', this.onDragStopBoundFunction);
		document.removeEventListener('mouseleave', this.onDragStopBoundFunction);
		document.removeEventListener('mouseout', this.onDragStopBoundFunction);

		this._dragInput.removeListener('touchmove');
		this._dragInput.removeListener('mousemove');

		this._isDragging = false;
	}

	protected onInputMove(event: PIXI.interaction.InteractionEvent): void {
		const deltaY: number = event.data.global.y - this._dragStartY;

		this._dragStartY = event.data.global.y;

		this._yOffset += deltaY;
		this._yOffset = Math.max(Math.min(this._yOffset, this._maxYDrag), this._minYDrag);
		this._mainBody.style.webkitTransform = `translateY(${this._yOffset}px)`;
		this._mainBody.style.transform = `translateY(${this._yOffset}px)`;

		const positionScalar: number = GameUtils.normalize(this._yOffset, 0, this._minYDrag);

		this._scrollBar.moveBarObject(1 - positionScalar);
	}

	protected replaceImages(): void {
		const images = this._mainBody.getElementsByTagName('img');
		// }

		let originalPath = '';

		// tslint:disable-next-line: prefer-for-of
		for (let i = 0; i < images.length; i++) {
			const image: HTMLImageElement = images[i];
			originalPath = image.getAttribute('src').replace('./', '');

			const imageResource: string = this.resources.getDevicePathForResource(this._resourcesPath + originalPath);
			image.setAttribute('src', imageResource);
		}
	}

	protected resizeHTML(): void {
		const scale = parseFloat(this._gameCanvas.style.width) / this.resolutionManager.resolution.width;
		this._mainContainer.style.transform = `scale(${scale})`;
		this._mainContainer.style.top = `${this._uiStageOffsetY * scale}px`;
		this._mainContainer.style.marginLeft = `${this._gameCanvas.style.marginLeft}`;
		this._mainContainer.style.marginTop = `${this._gameCanvas.style.marginTop}`;
	}

	protected goToPage(id: string): void {
		// TODO: If time,
		// Maybe implement as a regular HTML goto.
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/html/HTMLPageView.ts